import { EModeCategory, EModeState } from '@/domain/market-info/marketInfo'
import BigNumber from 'bignumber.js'

interface CalculatePooledSpendPowerUSDParams {
  collateralAmountUSD: BigNumber.Value
  maxLtv: BigNumber.Value
  remainingBorrowCapacityUSD: BigNumber.Value
  hasDebt: boolean
  usageAsCollateralEnabledOnUser: boolean
  eModeCategory?: EModeCategory
  eModeState: EModeState
}

function getEffectiveMaxLtv({
  maxLtv,
  eModeCategory,
  eModeState,
}: Pick<CalculatePooledSpendPowerUSDParams, 'maxLtv' | 'eModeCategory' | 'eModeState'>): BigNumber {
  if (eModeState.enabled && eModeCategory?.id === eModeState.category.id) {
    return new BigNumber(eModeState.category.ltv.toString())
  }

  return new BigNumber(maxLtv)
}

export function calculatePooledSpendPowerUSD({
  collateralAmountUSD,
  maxLtv,
  remainingBorrowCapacityUSD,
  hasDebt,
  usageAsCollateralEnabledOnUser,
  eModeCategory,
  eModeState,
}: CalculatePooledSpendPowerUSDParams): BigNumber {
  const collateralUSD = new BigNumber(collateralAmountUSD)
  if (!collateralUSD.isFinite() || collateralUSD.lte(0)) return new BigNumber(0)

  if (!hasDebt || !usageAsCollateralEnabledOnUser) {
    return collateralUSD
  }

  const effectiveMaxLtv = getEffectiveMaxLtv({ maxLtv, eModeCategory, eModeState })
  if (effectiveMaxLtv.lte(0)) {
    return collateralUSD
  }

  const availableBorrowCapacityUSD = new BigNumber(remainingBorrowCapacityUSD)
  if (availableBorrowCapacityUSD.lte(0)) return new BigNumber(0)

  return BigNumber.max(BigNumber.min(collateralUSD, availableBorrowCapacityUSD.div(effectiveMaxLtv)), 0)
}
